import * as Axios from 'axios'
import * as LodashFP from 'lodash/fp'

import { IStringTMap, IStringAnyMap } from '../types/map'
import { translateWithUserLocale } from '../utils/i18n'

export interface IResultError<T> {
  type: 'error'
  payload: T
}

export interface IResultSuccess<T> {
  type: 'success'
  payload: T
}

export interface IAppError {
  statusCode: string
  title: string
  message: string
}

export type Result<S = {}, E = IAppError> = IResultSuccess<S> | IResultError<E>
export type FutureResult<S = {}, E = IAppError> = Promise<Result<S, E>>

export type Descriptive = {
  name: string
  title: string
  description: string
}

export type Labeled = {
  name: string
  label: string
}

export type Adapter<T> = {
  decode: (input: any, options: IStringAnyMap) => T
  encode: (entity: T) => any
}

export type Adapters<T> = IStringTMap<AdapterContext<T>>

export type AdapterContext<T> = IStringTMap<Adapter<T>> & {
  default: Adapter<T>
}

export type Encode<T> = (
  contexts: AdapterContext<T>,
  entity: T,
  context: string,
) => any
export type Decode<T> = (
  contexts: AdapterContext<T>,
  data: any,
  context: string,
) => T

export type HttpCommand =
  | {
      method: 'get'
      data?: IStringAnyMap
      config?: Axios.AxiosRequestConfig
      url: string
    }
  | {
      method: 'delete'
      config?: Axios.AxiosRequestConfig
      url: string
    }
  | {
      method: 'post'
      config?: Axios.AxiosRequestConfig
      url: string
      data: any
    }
  | {
      method: 'put'
      config?: Axios.AxiosRequestConfig
      url: string
      data: any
    }
  | {
      method: 'patch'
      config?: Axios.AxiosRequestConfig
      url: string
      data: any
    }

export function appError(params: Partial<IAppError> = {}) {
  return {
    statusCode: params.statusCode || '🙊',
    message:
      params.message ||
      translateWithUserLocale('error.something_went_wrong_apologetic'),
    title: translateWithUserLocale('whoops') + '!',
  }
}

export type NonEmptyArray<T> = {
  0: T
} & Array<T>

export function resultError<T>(e: T): IResultError<T> {
  return {
    type: 'error',
    payload: e,
  }
}

export function resultSuccess<T>(payload: T): IResultSuccess<T> {
  return {
    type: 'success',
    payload,
  }
}

export function isAppError(error: any): error is IAppError {
  return (
    LodashFP.isPlainObject(error) &&
    'statusCode' in error &&
    'message' in error &&
    'title' in error &&
    LodashFP.isString(error.statusCode) &&
    LodashFP.isString(error.message) &&
    LodashFP.isString(error.title)
  )
}

export function labeledFromApi(data: { value: string; label: string }) {
  return {
    name: data.value,
    label: data.label,
  }
}
